import { DateTime } from 'luxon';
import moment from 'moment';
import { toast } from 'vue3-toastify';

import type { ISelectedDays } from '@/interfaces/agency/rota';

export function formatDay(value?: string) {
  if (value) return moment(String(value)).format('DD');

  return '';
}

export function formatWeekdayShort(value?: string) {
  if (value) return moment(String(value)).format('ddd');

  return '';
}

export function formatWeekDay(value: string) {
  if (value) return moment(String(value), true).format('dddd');

  return '';
}

export function formatMonth(value: string) {
  if (value) return moment(String(value)).format('MM');

  return '';
}

export function formatYear(value: string) {
  if (value) return moment(String(value)).format('YYYY');

  return '';
}

export function formatDate(value: string | null, inputFormat = 'YYYY-MM-DD') {
  const dateFormat = localStorage.getItem('dateFormat') || 'DD-MM-YYYY';

  if (value) return moment(String(value), inputFormat).format(dateFormat);

  return '';
}

export function formatWeekRange(week: number, year: number): string {
  const dateFormat = localStorage.getItem('dateFormat') || 'DD-MM-YYYY';

  const startOfWeek = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
  const endOfWeek = moment(startOfWeek).endOf('isoWeek');

  return `${startOfWeek.format(dateFormat)} to ${endOfWeek.format(dateFormat)}`;
}

export function payloadDateFormat(value: string | null) {
  if (value) {
    const parsedDate = moment(value, 'DD/MM/YYYY', true); // 'true' indicates strict parsing

    if (parsedDate.isValid()) return parsedDate.format('YYYY-MM-DD[T]HH:mm:ss');
  }

  return value;
}

export function formatTime(value: string) {
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm A';

  const parsed = moment(value, 'HH:mm:ss'); // Always parse as 24-hour input
  if (!parsed.isValid()) return '';

  return parsed.format(timeFormat);
}

export function formatUTCTimeZone(value: string) {
  return moment.utc(value, 'HH:mm:ssZ', true).format('HH:mm');
}

export function payloadTimeFormat(value: string | null) {
  if (value) {
    if (moment(String(value)).isValid()) return value;

    return moment(String(value), 'HH:mm').format('HH:mm:ss');
  }

  return null;
}

export function formatDateToTime(value: string, formatted = false) {
  if (formatted) return moment(value).format('hh:mm A');

  return moment(value).format('HH:mm');
}

export function formatUTCDateToTime(value: string) {
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm A';

  if (value) return moment.utc(value).format(timeFormat);
}

export function formatUTCFullTimeZone(value: string | null) {
  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm A';

  if (value) return moment.utc(value).format(timeFormat);
}

export function formatLocale(value: string) {
  if (value) return moment(String(value)).format('lll');

  return '';
}

export function returnJoinDates(startDate: string, endDate: string) {
  let returnDate = formatDate(startDate);

  if (endDate) returnDate = returnDate.concat(' - ', formatDate(endDate));

  return returnDate;
}

export function returnJoinTimes(startTime: string, endTime: string) {
  let returnTime = formatTime(startTime);

  if (endTime) returnTime = returnTime.concat(' - ', formatTime(endTime));

  return returnTime;
}

export function formatDateToString(date?: string) {
  if (date) return moment(date).format('dddd - DD MMM, YYYY');

  return '';
}

export function formatRotaDay(payload: ISelectedDays | undefined) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const orderedDays = days.filter(day => Object.prototype.hasOwnProperty.call(payload, day));

  if (orderedDays.length === days.length) return 'All Days';
  else return orderedDays.join(', ');
}

// Convert camelCase to words with spaces // camelCase => Camel Case
export function formatCamelCase(value: unknown) {
  if (value === null || value === undefined) return '';

  return String(value)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str: string) => str.toUpperCase());
}

// Calculate age
export const calculateAge = (value: string, inputFormat = 'YYYY-MM-DD') => {
  if (value) {
    const parsedDays = moment(value, inputFormat).fromNow();

    return `${parsedDays.split(' ')[0]} ${parsedDays.split(' ')[1]}`;
  }
};

// Convert underscore to title case
export const formatTitleCase = (value: string) => {
  if (value) return value.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

// Truncate string by length 30 and add ellipsis
export const truncateText = (value: string, length = 30) => {
  if (value) return value.length > length ? `${value.substring(0, length)}...` : value;
};

// Handle image cropper upload
export function useImageUpload() {
  const isImageCropperDialogVisible = ref(false);
  const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const imageData = ref<{ file: File | null; name: string; type: string }>({
    file: null,
    name: '',
    type: ''
  });

  // Handle file selection
  const handleImageUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      const file = target.files[0];

      // Check if file type is allowed
      if (!allowedImageTypes.includes(file.type)) {
        toast.error('Invalid image format, Please upload a JPEG, PNG, or WEBP.');

        return;
      }

      imageData.value = { file, name: file.name, type: file.type };
      isImageCropperDialogVisible.value = true;
    }
  };

  // Open file input to select an image
  const uploadImage = () => {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', handleImageUpload);
    input.click();
  };

  return {
    imageData,
    isImageCropperDialogVisible,
    uploadImage
  };
}

// Status variant resolver
export const resolveUserStatusVariant = (value: string) => {
  if (value) {
    const statLowerCase = value?.toLowerCase();
    if (statLowerCase === 'pending') return 'warning';
    if (statLowerCase === 'active') return 'success';
    if (statLowerCase === 'inactive') return 'error';
    if (statLowerCase === 'not verified') return 'info';
    if (statLowerCase === 'unavailable') return 'grey';
    if (statLowerCase === 'approved') return 'success';
    if (statLowerCase === 'rejected') return 'error';

    return 'primary';
  }
};

// Role variant resolver
export const resolveUserRoleVariant = (value: string) => {
  if (value) {
    const statLowerCase = value?.toLowerCase();
    if (statLowerCase === 'client') return 'warning';
    if (statLowerCase === 'admin') return 'success';
    if (statLowerCase === 'manager') return 'info';
    if (statLowerCase === 'carer') return '#c934eb';
    if (statLowerCase === 'contact') return '#eb9bdc';
    if (statLowerCase === 'superadmin') return 'grey';
    if (statLowerCase === 'care_coordinator') return '#ebde34';
    if (statLowerCase === 'field_supervisor') return '#a2eb34';

    return 'primary';
  }
};

// Function to calculate table height
export const getTableHeight = (offset: number = 0): number => {
  return window.innerHeight - offset;
};

// Height configurations
export const tableHeight = {
  separateFilter: () => getTableHeight(260), // eg: Client or Carer list page
  contactDashboard: () => getTableHeight(268), // eg: Contact -> Dashboard page
  withFilter: () => getTableHeight(242), // eg: Clients -> Absences or Notes page
  withFilterSubMenu: () => getTableHeight(288), // eg: Clients -> Contacts page
  reportsWithFilter: () => getTableHeight(252), // eg: Reports -> Client or Carer page
  withOverviewMenu: () => getTableHeight(198), // eg: Clients or Carer -> Profile Overview page
  rotaWithFilter: () => getTableHeight(280), // eg: Rota -> Client or Carer page
  separateMenuWithFilter: () => getTableHeight(324), // eg: Settings -> Jobs page
  separateMenuWithFilterSubMenu: () => getTableHeight(373), // eg: Settings -> Medication page
  separateMenuForCompliance: () => getTableHeight(399) // eg: Settings -> Compliance -> Document Library page
};

// Convert 12 hour time to 24 hour
export const formatTime12To24 = (value: string | null) => {
  if (!value) return '';

  const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');
  const timeFormat = timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm A';

  return moment(value, timeFormat).format('HH:mm:ss');
};

// Shift date for Next and Previous days with
export const formatShiftDate = (date: string, days: number) => {
  if (!date) return '';

  return DateTime.fromFormat(date, 'yyyy-MM-dd').plus({ days }).toFormat('yyyy-MM-dd');
};

export const useVisibleItems = <T extends { id: number; [key: string]: any }>(
  source: ComputedRef<T[] | undefined>,
  selectedItems: ComputedRef<unknown[]>,
  maxVisible = 1,
  filterField = 'id'
) => {
  return computed(() => {
    const items = source.value ?? [];
    const selected = selectedItems.value ?? [];

    if (items.length === 0 || selected.length === 0) {
      return { visible: [], remaining: 0 };
    }

    const selectedObjects =
      filterField === 'id'
        ? items.filter(i => selected.includes(i.id))
        : items.filter(i =>
            selected.some(s => (s as Record<string, any>)[filterField] === i[filterField as keyof typeof i])
          );

    const totalSelected = selectedObjects.length;

    if (totalSelected === 0) {
      return { visible: [], remaining: 0 };
    }

    const visible =
      totalSelected <= maxVisible
        ? selectedObjects.map(i => i.id)
        : selectedObjects.slice(0, maxVisible).map(i => i.id);

    return {
      visible,
      remaining: Math.max(0, totalSelected - maxVisible)
    };
  });
};

export const capitalizeFirstWord = (text: string) => {
  if (!text) return '';
  text = text.trim();

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const weekdayOrder: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7
};

export const sortWeekdays = (days: string[]): string[] => {
  return [...days].sort((a, b) => weekdayOrder[a] - weekdayOrder[b]);
};

export const formatTimeAgo = (value: string | null) => {
  if (!value) return null;

  return DateTime.fromISO(value).toRelative();
};
